import useCheckDevice from "@repo/shopify/hooks/useCheckDevice";
import { getTotalProductCount } from "@repo/shopify/utils/collections";
import type { Filter, FilterValue } from "@shopify/hydrogen-react/storefront-api-types";
import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import Button from "@atoms/Button";
import Icon from "@atoms/Icon";

import FilterCard from "@molecules/Filter/FilterCard";
import Sort from "@molecules/Filter/Sort";

interface IProps {
  onClickClose: () => void;
  filters: Filter[];
}

// 쿼리 파라미터 키 ↔ Shopify 필터 label. isVisible이 있으면 노출 대상만 추린다
const FILTER_GROUPS: { key: string; label: string; isVisible?: (value: FilterValue) => boolean }[] = [
  { key: "category", label: "Category", isVisible: (value) => value.label !== "GWP" },
  { key: "size", label: "Size" },
  { key: "color", label: "Color", isVisible: (value) => value.id.includes("group") },
];

// 선택된 필터는 ["A","B"] 형태의 JSON 문자열로 파라미터에 담긴다
const parseParam = (param: string | null): string[] => {
  if (!param) return [];

  try {
    return JSON.parse(param) as string[];
  } catch {
    return [];
  }
};

export default function Filter({ onClickClose, filters }: IProps) {
  const [searchParams, setSearchParams] = useSearchParams();

  const { isPC } = useCheckDevice();

  const filterGroups = useMemo(
    () =>
      FILTER_GROUPS.map(({ key, label, isVisible }) => ({
        key,
        values: filters
          .filter((filter) => filter.label === label)
          .flatMap((filter) => filter.values)
          .filter((value) => isVisible?.(value) ?? true),
      })),
    [filters],
  );

  const handleFilter = (option: FilterValue, key: string) => {
    const prevValues = parseParam(searchParams.get(key));
    const nextValues = prevValues.includes(option.label)
      ? prevValues.filter((value) => value !== option.label)
      : [...prevValues, option.label];

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);

      if (nextValues.length) next.set(key, JSON.stringify(nextValues));
      else next.delete(key);

      return next;
    });
  };

  const totalProducts = useMemo(() => getTotalProductCount(filters), [filters]);

  return (
    <>
      {isPC ? (
        <section className="flex h-fit w-full flex-col gap-10">
          <Button as="wrapper" className="items-center gap-2" onClick={onClickClose}>
            <Icon className="h-3.5 dark:text-white" name="LeftArrow" />
            <span>{`Hide Filter & Sort (${totalProducts})`}</span>
          </Button>
          <Sort />
          {filterGroups.map(({ key, values }) => (
            <FilterCard
              filterKey={key}
              filterList={values}
              key={key}
              onChangeFilter={handleFilter}
              selected={parseParam(searchParams.get(key))}
            />
          ))}
        </section>
      ) : (
        <section className="dark:bg-dj-black fixed inset-0 z-70 flex flex-col bg-white">
          <div className="tnt-gap-x grid grid-cols-8 items-center p-5">
            <Button as="wrapper" className="col-span-1" onClick={onClickClose}>
              <Icon className="h-3.5 dark:text-white" name="LeftArrow" />
            </Button>
            <Button as="wrapper" className="col-span-7" onClick={onClickClose}>
              <span>{`Hide Filter & Sort (${totalProducts})`}</span>
            </Button>
          </div>
          {/*</div>*/}
          <div className="tnt-gap-x grid grid-cols-8 overflow-y-auto">
            <div className="col-span-7 col-start-2 flex flex-col gap-10 p-4 pt-0">
              <Sort />
              {filterGroups.map(({ key, values }) => (
                <FilterCard
                  filterKey={key}
                  filterList={values}
                  key={key}
                  onChangeFilter={handleFilter}
                  selected={parseParam(searchParams.get(key))}
                />
              ))}
            </div>
          </div>
          <div className="p-4 pt-0">
            <Button as="square" className="w-full" onClick={onClickClose}>
              Apply
            </Button>
          </div>
        </section>
      )}
    </>
  );
}
